import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Key, Plus, Pencil, Trash2, Save, X, RefreshCw, ChevronLeft, ChevronRight, Eye, Search, GripVertical, KeyRound, LayoutGrid, AlertTriangle, Route } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useNotifications } from '../../context/NotificationContext';
import { adminManagementApi, appCfgApi } from '../../services/api';
import StuiSelect from '../../components/common/StuiSelect';
import { usePermissionMap, KeyUses, GridSection } from './permissionMap';
import { KeyMatrixTab } from './AccessMatrix';

/**
 * Administration > Security > Permissions. SysAdmin only.
 *
 *   Keys      the key catalog (AppSec_DefPermissions): add and describe keys, and for the selected one
 *             what it opens — pages, sections, tables, the write check, checks written in code — read
 *             by the server from what is enforced (the permission map), so it cannot drift from the code.
 *   Matrix    every page against every action and the keys each cell takes (AccessMatrix); one person's
 *             answers are on the Users page.
 *   Problems  what the map found wrong. Empty means clean.
 *
 * The action menu's "How endpoints are guarded" lists every API endpoint no key guards and what does.
 */
const TABS = [
  { id: 'keys', label: 'Keys', icon: KeyRound },
  { id: 'matrix', label: 'Matrix', icon: LayoutGrid },
  { id: 'problems', label: 'Problems', icon: AlertTriangle },
];

const RULES = [
  ['Key', 'by a key'], ['SysAdmin', 'SysAdmin only'], ['Table', 'by the table check'],
  ['Database', 'by a role on the database'], ['SignedIn', 'open to anyone signed in'], ['InCode', 'checked in their own code'],
];

const ProblemsTab = ({ map }) => (
  <div className="stui-tab-body">
    <GridSection title={`What the map found (${map.problems.length})`} empty="Nothing found: every key gates something the API enforces, and every table a page writes has an owner."
      columns={[{ label: 'Key or row', width: '28%' }, { label: 'Problem' }]}
      rows={map.problems.map(p => {
        const i = p.indexOf(': ');
        return { key: p, cells: i < 0 ? ['', p] : [p.slice(0, i), p.slice(i + 2)] };
      })} />
  </div>
);

/** Every API endpoint by what guards it, and the ones no key guards with what does. An inventory, not a fault list. */
const EndpointGuardsModal = ({ map, onClose }) => {
  const total = Object.values(map.endpointCounts).reduce((a, b) => a + b, 0);
  return (
    <div className="stui-modal-overlay">
      <div className="stui-modal stui-modal-xxl">
        <div className="stui-modal-header">
          <h3>How Endpoints Are Guarded</h3>
          <button className="stui-modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="stui-modal-body">
          <p className="stui-hint">
            {total} API endpoints: {RULES.filter(([r]) => map.endpointCounts[r]).map(([r, words]) => `${map.endpointCounts[r]} ${words}`).join(', ')}.
            A test fails the build when an endpoint has no rule.
          </p>
          <GridSection title={`Endpoints no key guards (${map.unkeyed.length})`} height="60vh"
            columns={[{ label: 'Controller', width: '16%' }, { label: 'Method', width: '9%' }, { label: 'Endpoint', width: '30%' }, { label: 'What protects it' }]}
            rows={map.unkeyed.map(e => ({ key: e.methods + e.route + e.action, cells: [e.controller, e.methods, `/${e.route}`, e.how] }))} />
        </div>
        <div className="stui-modal-footer">
          <button className="stui-btn stui-btn-primary" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

const PermissionsPage = () => {
  const [permissions, setPermissions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [editing, setEditing] = useState(null);
  const [dirtyFields, setDirtyFields] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [listCollapsed, setListCollapsed] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [matrixRefresh, setMatrixRefresh] = useState(0);
  const [showGuards, setShowGuards] = useState(false);
  // Category options are the Permission_Category lookup values — the same list the API validates against.
  const [categories, setCategories] = useState([]);
  const menuRef = useRef(null);
  // ?tab=matrix (or problems) opens on that tab; the navigation editor links to the matrix.
  const location = useLocation();
  const [tab, setTab] = useState(() => {
    const asked = new URLSearchParams(location.search).get('tab');
    return TABS.some(t => t.id === asked) ? asked : 'keys';
  });
  const { map, error: mapError, reload: reloadMap } = usePermissionMap();

  const { notify } = useNotifications();

  useEffect(() => {
    const handleClickOutside = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [res, cats] = await Promise.all([
        adminManagementApi.getPermissions(),
        appCfgApi.getLookupsByCategory('Permission_Category'),
      ]);
      setPermissions(res.data);
      setCategories((cats.data || []).filter(c => c.is_Active).map(c => c.value));
    } catch (err) {
      console.error('Failed to load permissions:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const startEdit = () => {
    if (!selected) return;
    setEditing({ ID: selected.id, PermissionKey: selected.permissionKey, Category: selected.category, DisplayName: selected.displayName, Description: selected.description || '', GrantScope: selected.grantScope || 'Both' });
    setDirtyFields(new Set());
  };

  const startAdd = () => {
    setSelected(null);
    setEditing({ PermissionKey: '', Category: '', DisplayName: '', Description: '', GrantScope: 'Both' });
    setDirtyFields(new Set(['PermissionKey', 'Category', 'DisplayName']));
  };

  const cancelEdit = (force = false) => {
    if (!force && dirtyFields.size > 0) { notify.confirm('Discard unsaved changes?', () => { setEditing(null); setDirtyFields(new Set()); }); return; }
    setEditing(null); setDirtyFields(new Set());
  };

  const handleFieldChange = (field, value) => {
    setEditing(prev => ({ ...prev, [field]: value }));
    const original = selected ? (field === 'Description' ? (selected.description || '') : selected[field.charAt(0).toLowerCase() + field.slice(1)]) : '';
    const newDirty = new Set(dirtyFields);
    if (value !== original || !selected) { newDirty.add(field); } else { newDirty.delete(field); }
    setDirtyFields(newDirty);
  };

  const handleSave = async () => {
    if (!editing || dirtyFields.size === 0) return;
    // Duplicate key check on new permissions
    if (!editing.ID) {
      const dupe = permissions.find(p => p.permissionKey.toLowerCase() === editing.PermissionKey.trim().toLowerCase());
      if (dupe) { notify.error(`Permission key "${editing.PermissionKey}" already exists.`); return; }
    }
    setSaving(true);
    try {
      if (editing.ID) {
        await adminManagementApi.updatePermission(editing.ID, editing);
      } else {
        await adminManagementApi.createPermission(editing);
      }
      const savedId = editing.ID;
      setEditing(null);
      setDirtyFields(new Set());
      const res = await adminManagementApi.getPermissions();
      setPermissions(res.data);
      if (savedId) { const refreshed = res.data.find(p => p.id === savedId); if (refreshed) setSelected(refreshed); }
      else setSelected(null);
    } catch (err) {
      console.error('Save failed:', err);
      notify.error(err.response?.data?.message || 'Failed to save permission');
    } finally {
      setSaving(false);
    }
  };

  const isEditing = editing !== null;
  const hasDirty = dirtyFields.size > 0;

  const term = searchTerm.toLowerCase();
  const filtered = permissions.filter(p =>
    !term || [p.displayName, p.permissionKey, p.category, p.description].some(v => v?.toLowerCase().includes(term))
  );
  const entryFor = key => map?.keys.find(k => k.key === key);
  const flagged = key => (entryFor(key)?.flags.length || 0) > 0;

  if (loading) return <div className="stui-detail-empty"><Key size={48} /><p>Loading...</p></div>;

  return (
    <div className="stui-module-container">
      <div className="stui-module-header">
        <div className="stui-module-header-left">
          <Key size={20} />
          <h1>Permissions</h1>
          <div className="stui-module-header-tabs">
            {TABS.map(({ id, label, icon: Icon }) => (
              <button key={id} className={`stui-dataentry-tab${tab === id ? ' active' : ''}`} disabled={isEditing && id !== tab}
                onClick={() => setTab(id)}>
                <Icon size={14} /><span>{label}{id === 'problems' && map ? ` (${map.problems.length})` : ''}</span>
              </button>
            ))}
          </div>
          {tab === 'keys' && <span className="stui-module-header-meta">{permissions.length} permissions</span>}
        </div>
        <div className="stui-module-header-actions">
          {tab !== 'keys' ? null : isEditing ? (
            // Permissions are never deleted here: the key is hard-coded across the app, so removing
            // one is a deliberate developer change made with a script.
            <button className="stui-crud-btn stui-delete-btn disabled" disabled
              title="Permissions cannot be deleted here. Removing a key is a code change a developer makes deliberately."
            ><Trash2 size={18} /></button>
          ) : (
            <button className="stui-crud-btn" onClick={startAdd} title="Add permission"><Plus size={18} /></button>
          )}
          {tab === 'keys' && (
            <>
              <button className={`stui-crud-btn ${!isEditing && !selected ? 'disabled' : ''}`} onClick={isEditing ? cancelEdit : startEdit} disabled={!selected && !isEditing} title={isEditing ? 'Cancel' : 'Edit'}>
                {isEditing ? <X size={18} /> : <Pencil size={18} />}
              </button>
              <button className={`stui-crud-btn ${!hasDirty ? 'disabled' : ''}`} onClick={handleSave} disabled={!hasDirty || saving} title="Save"><Save size={18} /></button>
            </>
          )}
          <div className="stui-action-menu" ref={menuRef}>
            <button className="stui-action-menu-btn" onClick={() => setMenuOpen(!menuOpen)} title="Actions"><GripVertical size={16} /></button>
            {menuOpen && (
              <div className="stui-action-menu-dropdown">
                <button className="stui-action-menu-item" onClick={() => { setMenuOpen(false); load(); reloadMap(); setMatrixRefresh(n => n + 1); }}><RefreshCw size={16} /><span>Refresh</span></button>
                <button className="stui-action-menu-item" disabled={!map} onClick={() => { setMenuOpen(false); setShowGuards(true); }}><Route size={16} /><span>How endpoints are guarded</span></button>
              </div>
            )}
          </div>
        </div>
      </div>

      {tab !== 'keys' && mapError && <div className="stui-alert stui-alert-error">{mapError}</div>}
      {tab === 'matrix' && <KeyMatrixTab refresh={matrixRefresh} />}
      {showGuards && map && <EndpointGuardsModal map={map} onClose={() => setShowGuards(false)} />}
      {tab === 'problems' && (map ? <ProblemsTab map={map} /> : !mapError && <div className="stui-detail-empty"><AlertTriangle size={48} /><p>Reading what the API enforces...</p></div>)}

      {tab === 'keys' && <div className="stui-list-view">
        {listCollapsed ? (
          <div className="stui-list-panel stui-collapsed">
            <button onClick={() => setListCollapsed(false)} className="stui-list-panel-expand-btn" title="Expand"><ChevronRight size={16} /></button>
          </div>
        ) : (
          <div className="stui-list-panel">
            <div className="stui-list-panel-header">
              <button onClick={() => setListCollapsed(true)} className="stui-list-panel-collapse-btn" title="Collapse"><ChevronLeft size={16} /></button>
              <div className="stui-search-wrapper">
                <Search size={14} className="stui-search-icon" />
                <input className="stui-field-input" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search permissions..." />
              </div>
            </div>
            <div className="stui-list-scroll">
              {filtered.length === 0 ? (
                <div className="stui-list-empty">No permissions found</div>
              ) : filtered.map(p => (
                <button key={p.id} onClick={() => { setSelected(p); setEditing(null); setDirtyFields(new Set()); }} className={`stui-list-item ${selected?.id === p.id ? 'selected' : ''}`}>
                                      <span className="stui-list-item-primary">{p.displayName || p.permissionKey}{flagged(p.permissionKey) && <> <AlertTriangle size={12} className="stui-text-muted" /></>}</span>
                    <span className="stui-list-item-secondary">
                      {p.category} • {p.permissionKey}
                      {p.grantScope && p.grantScope !== 'Both' && (
                        <span className="perm-scope-tag">{p.grantScope} only</span>
                      )}
                    </span>
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="stui-detail-panel">
          {isEditing ? (
            <div className="stui-detail-content">
              <div className="stui-detail-grid">
                <div className="stui-col-24"><div className="stui-detail-section-header">{editing.ID ? 'Edit Permission' : 'New Permission'}</div></div>
                <div className="stui-field-group stui-col-8">
                  <label>Category</label>
                  <StuiSelect className={`stui-field-input${dirtyFields.has('Category') ? ' dirty' : ''}`} value={editing.Category} onChange={e => handleFieldChange('Category', e.target.value)}>
                    {!editing.Category && <option value="">Select a category...</option>}
                    {editing.Category && !categories.includes(editing.Category) && (
                      <option value={editing.Category}>{editing.Category} (not in list)</option>
                    )}
                    {categories.map(c => <option key={c} value={c}>{c}</option>)}
                  </StuiSelect>
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Display Name</label>
                  <input className={`stui-field-input${dirtyFields.has('DisplayName') ? ' dirty' : ''}`} value={editing.DisplayName} onChange={e => handleFieldChange('DisplayName', e.target.value)} />
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Permission Key</label>
                  {editing.ID ? (
                    <input className="stui-field-input" value={editing.PermissionKey} readOnly title="Key is immutable after creation" />
                  ) : (
                    <input className={`stui-field-input${dirtyFields.has('PermissionKey') ? ' dirty' : ''}`} value={editing.PermissionKey} onChange={e => handleFieldChange('PermissionKey', e.target.value)} placeholder="e.g. reports.submittals" />
                  )}
                </div>
                <div className="stui-field-group stui-col-8">
                  <label>Grant Scope</label>
                  <StuiSelect className={`stui-field-input${dirtyFields.has('GrantScope') ? ' dirty' : ''}`} value={editing.GrantScope || 'Both'} onChange={e => handleFieldChange('GrantScope', e.target.value)}
                          title="Where this permission may be granted. Global keys cannot be granted per instance or by role default; Instance keys cannot be granted globally.">
                    <option value="Both">Both — per instance or globally</option>
                    <option value="Global">Global only — never per instance</option>
                    <option value="Instance">Instance only — never globally</option>
                  </StuiSelect>
                </div>
                <div className="stui-field-group stui-col-24">
                  <label>Description</label>
                  <textarea className={`stui-field-input${dirtyFields.has('Description') ? ' dirty' : ''}`} value={editing.Description} onChange={e => handleFieldChange('Description', e.target.value)} rows={3} />
                </div>
              </div>
            </div>
          ) : selected ? (
            <div className="stui-detail-content">
              <div className="stui-detail-grid">
                <div className="stui-col-24"><div className="stui-detail-section-header">Permission Details</div></div>
                <div className="stui-field-group stui-col-8"><label>Category</label><input className="stui-field-input" value={selected.category || ''} readOnly /></div>
                <div className="stui-field-group stui-col-8"><label>Display Name</label><input className="stui-field-input" value={selected.displayName || ''} readOnly /></div>
                <div className="stui-field-group stui-col-8"><label>Permission Key</label><input className="stui-field-input" value={selected.permissionKey} readOnly /></div>
                <div className="stui-field-group stui-col-8"><label>Grant Scope</label><input className="stui-field-input" value={selected.grantScope || 'Both'} readOnly /></div>
                <div className="stui-field-group stui-col-16"><label>Role Defaults</label>
                  <input className="stui-field-input" readOnly
                    value={!map ? '' : entryFor(selected.permissionKey)?.roles.join(', ') || 'None: granted per person only'} /></div>
                {selected.description && <div className="stui-field-group stui-col-24"><label>Description</label><textarea className="stui-field-input" value={selected.description} readOnly rows={3} /></div>}
              </div>
              {map ? <KeyUses entry={entryFor(selected.permissionKey)} />
                : <p className="stui-hint">{mapError || 'Reading what the API enforces...'}</p>}
            </div>
          ) : (
            <div className="stui-detail-empty"><Eye size={48} /><p>Select a permission to see what it opens</p><p className="stui-hint">Or click + to define a new one</p></div>
          )}
        </div>
      </div>}
    </div>
  );
};

export default PermissionsPage;
